/**
 * Toast — the one in-page notice for a finished or failed action.
 *
 * Replaces window.alert: it never blocks the page and goes away on its own,
 * so a wall screen with nobody at it is not stuck behind a dialog. Shown by
 * DialogProvider; call sites use useNotice() and never render this directly.
 */
import React from 'react';
import styled from '@emotion/styled';
import { keyframes } from '@emotion/react';
import { AlertCircle, CheckCircle2, Info, X } from 'lucide-react';
import { useTranslation } from '../hooks/useTranslation';

const slideUp = keyframes`
  from { opacity: 0; transform: translateY(12px); }
  to { opacity: 1; transform: translateY(0); }
`;

const Stack = styled.div`
  position: fixed;
  left: 50%;
  bottom: 28px;
  transform: translateX(-50%);
  z-index: 1200; /* above ModalWrapper (1000) and ConfirmDialog (1100) */
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 10px;
  width: min(560px, calc(100vw - 32px));
  pointer-events: none;
`;

const TONES = {
  error: { bg: 'rgba(45, 16, 18, 0.96)', border: 'rgba(239, 83, 80, 0.55)', fg: '#FFB4B0', Icon: AlertCircle },
  success: { bg: 'rgba(14, 40, 24, 0.96)', border: 'rgba(76, 175, 80, 0.55)', fg: '#B9F0BE', Icon: CheckCircle2 },
  info: { bg: 'rgba(22, 33, 62, 0.96)', border: 'rgba(255, 255, 255, 0.18)', fg: 'rgba(255, 255, 255, 0.9)', Icon: Info },
};

const Card = styled.div`
  pointer-events: auto;
  display: flex;
  align-items: flex-start;
  gap: 12px;
  width: 100%;
  padding: 12px 14px;
  border-radius: 12px;
  background: ${(p) => TONES[p.$tone].bg};
  border: 1px solid ${(p) => TONES[p.$tone].border};
  box-shadow: 0 10px 34px rgba(0, 0, 0, 0.5);
  color: ${(p) => TONES[p.$tone].fg};
  font-size: 0.95rem;
  line-height: 1.45;
  white-space: pre-line;
  animation: ${slideUp} 0.18s ease-out;

  svg:first-of-type {
    flex-shrink: 0;
    margin-top: 2px;
  }
`;

const Text = styled.div`
  flex: 1;
  min-width: 0;
  overflow-wrap: anywhere;
`;

const Close = styled.button`
  flex-shrink: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  width: 28px;
  height: 28px;
  margin: -4px -6px -4px 0;
  border: none;
  border-radius: 8px;
  background: none;
  color: inherit;
  opacity: 0.6;
  cursor: pointer;
  &:hover { opacity: 1; background: rgba(255, 255, 255, 0.1); }
`;

export function Toast({ tone = 'info', message, onClose }) {
  const { t } = useTranslation();
  const { Icon } = TONES[tone] || TONES.info;
  return (
    <Card $tone={TONES[tone] ? tone : 'info'} role={tone === 'error' ? 'alert' : 'status'}>
      <Icon size={18} />
      <Text>{message}</Text>
      <Close type="button" onClick={onClose} aria-label={t('common.close')}>
        <X size={16} />
      </Close>
    </Card>
  );
}

export default function ToastStack({ notices, onClose }) {
  if (!notices.length) return null;
  return (
    <Stack>
      {notices.map((n) => (
        <Toast key={n.id} tone={n.tone} message={n.message} onClose={() => onClose(n.id)} />
      ))}
    </Stack>
  );
}
